/**
 * Shared yield heatmap bucketing: min → p95 in 5 steps (same logic for map colors and legend)
 */

// [R, G, B, A] for deck.gl ScatterplotLayer (ScatterplotLayer expects rgba values)
const YIELD_BUCKET_RGBA = [
    [255, 0, 0, 255],
    [255, 165, 0, 255],
    [255, 255, 0, 255],
    [144, 238, 144, 255],
    [0, 128, 0, 255],
]

// Same colors as CSS strings for the legend (legend uses backgroundColor)
const YIELD_BUCKET_CSS = [
    'rgb(255, 0, 0)',
    'rgb(255, 165, 0)',
    'rgb(255, 255, 0)',
    'rgb(144, 238, 144)',
    'rgb(0, 128, 0)',
]

// When we cannot form bands (no data or all values are equal), use a single color
const YELLOW_FALLBACK = [255, 255, 0, 255]

export function getYieldBucketsFromPoints(points) {
    if (!points?.length) {
        return null
    }
    const values = points
        .map((p) => p.value)
        .filter((v) => Number.isFinite(v))
        .sort((a, b) => a - b)

    if (!values.length) {
        return null
    }

    const min = values[0]
    // Avoid raw max: outliers would squash the whole color scale to one bucket
    // P95 = value at the 95th percentile index in the sorted array (a realistic “near max”)
    // const p95 = values[Math.floor(values.length * 0.95)] ?? min
    const p95 = values[Math.floor(values.length - 1)] ?? min // last value in the array is the max

    // Degenerate: min equals p95 → no range to split into 5 buckets
    if (p95 <= min) {
        return { degenerate: true, min }
    }

    // Split only [min, p95] into 5 equal parts; last bucket = everything at or above b4
    const step = (p95 - min) / 5

    return {
        degenerate: false,
        min,
        p95,
        step,
        // Thresholds: value falls into [min,b1), [b1,b2), ..., [b4, +∞)
        b1: min + step,
        b2: min + step * 2,
        b3: min + step * 3,
        b4: min + step * 4,
    }
}

export function makeYieldColorFnFromBuckets(buckets) {
    if (!buckets) {
        return () => YELLOW_FALLBACK
    }
    if (buckets.degenerate) {
        return () => YELLOW_FALLBACK
    }
    const { b1, b2, b3, b4 } = buckets
    return (value) => {
        // Compare from lowest threshold up (first matching branch wins)
        if (value < b1) return YIELD_BUCKET_RGBA[0]
        if (value < b2) return YIELD_BUCKET_RGBA[1]
        if (value < b3) return YIELD_BUCKET_RGBA[2]
        if (value < b4) return YIELD_BUCKET_RGBA[3]
        return YIELD_BUCKET_RGBA[4]
    }
}

export function makeYieldColorFnFromPoints(points) {
    return makeYieldColorFnFromBuckets(getYieldBucketsFromPoints(points))
}

export function getYieldLegendRowsFromBuckets(buckets, formatValue) {
    if (buckets.degenerate) {
        return [
            {
                color: 'rgb(255, 255, 0)',
                label: `${formatValue(buckets.min)} - ${formatValue(buckets.min)}`,
            },
        ]
    }

    const { min, p95, step } = buckets
    // 6 numbers → 5 intervals (each legend row = one color between bounds[i] and bounds[i + 1])
    const bounds = [
        min,
        min + step,
        min + step * 2,
        min + step * 3,
        min + step * 4,
        p95,
    ]

    return YIELD_BUCKET_CSS.map((color, index) => ({
        color,
        label: `${formatValue(bounds[index])} - ${formatValue(bounds[index + 1])}`,
    }))
}

export function hexToRgba(hex, opacity = 1) {
    const clean = (hex || '#888888').replace('#', '')
    const bigint = parseInt(clean, 16)

    const r = (bigint >> 16) & 255
    const g = (bigint >> 8) & 255
    const b = bigint & 255
    const a = Math.round(opacity * 255)

    return [r, g, b, a]
}
